import { useEffect, useRef, useState } from 'react';
import {
  LoaderStatus,
  type LoaderState,
  type PypeLoader,
  type SessionInfo,
} from '@pypestream/pype-loader';

import { buildEngagementApiUrl } from '../../utils';

export type LivePypeStatus = 'booting' | 'ready' | 'error';

/** Bounded so a widget that never offers `restart` cannot spin forever. */
const RESTART_RETRY_MS = 150;
const MAX_RESTART_ATTEMPTS = 13; // ~2s

/**
 * Tear a Loader down the way the Pype Designer does, and for its reasons.
 *
 * `shutdown()` is not always permitted — `can()` gates it on the Loader's own
 * state — so an unguarded call throws, and a throw here would leave the widget
 * mounted. `reset()` is the fallback. The DOM sweep afterwards is the part
 * that is easy to skip and expensive to miss: a Loader that failed to tear
 * itself down leaves its `<ps-pype>` behind, and the next boot mounts a SECOND
 * widget into the same pane.
 */
function forceUnmount(loader: PypeLoader, container: HTMLElement): void {
  try {
    if (loader.can('shutdown')) {
      loader.shutdown();
    } else if (loader.can('reset')) {
      loader.reset();
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      '[test-surface] Loader teardown threw; falling back to DOM removal.',
      err
    );
  }
  for (const el of Array.from(container.querySelectorAll('ps-pype'))) {
    el.remove();
  }
}

/**
 * Boot a REAL Pype widget into a bounded element, against a real channel.
 *
 * This is the same embed the Pype Designer performs
 * (`pype-designer-container.tsx`); the differences are deliberate and each one
 * matters:
 *
 * - **`anchorTo: 'container'`** — without it the widget's fixed-position chrome
 *   docks to the Pro Studio page instead of the pane. `<ps-pype>` then takes
 *   both `contain: layout` and `container-type: size`; neither substitutes for
 *   the other (see pype's `render-boundary.ts`).
 * - **`launcher: 'none'`** — headless. The panel fills the container and the
 *   modality sends `LAUNCH` itself on mount, so the conversation is on screen
 *   with nothing to click. A `'default'` launcher would render a bubble in the
 *   corner of the pane and wait.
 * - **The design IS fetched.** The designer passes `skipDesignFetch: true`
 *   because it streams in-progress styling itself; here the point is to see the
 *   solution as a customer would, so the persisted design must load.
 *
 * The container must have a definite height — size containment means a
 * percentage height against an auto-height ancestor resolves to 0, not to
 * content height.
 */
export const useLivePype = (
  appId: string | null,
  channelEnv: 'live' | 'sandbox'
): {
  containerRef: React.RefObject<HTMLDivElement>;
  status: LivePypeStatus;
  error: string | null;
  /**
   * The Loader's own status, surfaced for diagnosis. A pane that boots and
   * then refuses input looks the same whether the conversation never started,
   * started and ended, or restored an already-ended one — and those have
   * different causes. This is the only thing on screen that tells them apart.
   */
  loaderStatus: LoaderState['status'] | null;
  /**
   * The conversation the widget is actually having.
   *
   * This is the correlation key for everything on the results side: the
   * Phoenix topic is `chat_v2:${chatId}`, and it is how a Langfuse trace is
   * matched to the conversation on screen rather than to some other tab's.
   *
   * It has to come from the Loader rather than be minted here — the widget
   * owns the session, and a second one created alongside it would be a
   * different conversation against the same solution, which is the mistake
   * this surface has already made once.
   */
  session: SessionInfo | null;
} => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<LivePypeStatus>('booting');
  const [error, setError] = useState<string | null>(null);
  const [loaderStatus, setLoaderStatus] = useState<
    LoaderState['status'] | null
  >(null);
  const [session, setSession] = useState<SessionInfo | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !appId) return undefined;

    let cancelled = false;
    // Captured locally so cleanup tears down the Loader THIS effect booted,
    // never one a later run created.
    //
    // pro-studio runs in StrictMode, so this effect mounts, tears down and
    // mounts again in dev. VERIFIED by tracing it, not assumed: the first
    // pass logs "no loader had been created", because `cancelled` is set
    // before the dynamic `import()` below resolves. Keep that import async —
    // making it synchronous would let the first pass build a Loader that the
    // immediate teardown then shuts down, taking the session with it.
    let myLoader: PypeLoader | null = null;
    let offReady: (() => void) | null = null;
    let offState: (() => void) | null = null;
    let restartTimer: ReturnType<typeof setTimeout> | undefined;
    let restartAttempts = 0;

    setStatus('booting');
    setError(null);
    // The DIAGNOSTIC line is per-channel too. Left alone, switching channels
    // kept the previous conversation's chat id and Loader status on screen
    // under the new channel's name until the new widget reported — which is
    // precisely the correlation key someone reads off that line.
    setSession(null);
    setLoaderStatus(null);

    // Fatal: nothing mounted and nothing will. Reserved for the dynamic import
    // failing — see the `.catch` below.
    const failFatally = (message: string) => {
      if (cancelled) return;
      setError(message);
      setStatus('error');
    };

    // Non-fatal: the widget reported a problem it may well recover from.
    //
    // Pype's contract is "speak, don't throw" — `onError` is a NOTIFICATION,
    // and the machine owns retry and recovery (a 403 clears the session and
    // re-creates it; a socket drop backs off and reconnects). Treating every
    // one as terminal put a full-pane error over a widget that was still
    // starting, which `messenger:ready` then cleared a moment later: the
    // error flashed and vanished.
    //
    // The widget renders its own unavailable state inside the pane when it
    // genuinely gives up, so covering it with a second opinion is both
    // redundant and worse — it hides the real UI. Record and log instead.
    const reportRecoverable = (message: string) => {
      if (cancelled) return;
      setError(message);
      // eslint-disable-next-line no-console
      console.warn('[test-surface] pype reported an error:', message);
    };

    import('@pypestream/pype-loader')
      .then(({ createPypeLoader }) => {
        if (cancelled) return;

        const loader = createPypeLoader({
          appId,
          container,
          modality: 'floating',
          launcher: 'none',
          anchorTo: 'container',
          engagementApiUrl: buildEngagementApiUrl(
            window.location.origin,
            channelEnv
          ),
          // Required for the line above to have ANY effect, despite the name.
          // `preview` is not a mock mode — it does exactly two things in pype,
          // and both are "honour the runtime engagement-API override": the
          // session (`pype-machine.ts`, `engagementApiUrlOverride: ctx.preview
          // ? … : undefined`) and the socket (`phoenix.ts`,
          // `connectAndJoinChannel`). Without it the override is discarded and
          // the widget falls back to `PYPE_ENGAGEMENT_API_URL`, baked in at
          // pype's build time — which a local build usually lacks, so session
          // creation throws "Missing PYPE_ENGAGEMENT_API_URL".
          //
          // Even with that constant present it would be the wrong answer here:
          // it is ONE value, while this surface has to follow each channel to
          // its own live/sandbox stack.
          preview: true,
          onError: (err) => reportRecoverable(err.message),
        });

        myLoader = loader;
        offReady = loader.on('messenger:ready', () => {
          if (cancelled) return;
          setStatus('ready');
          // The shell mounted, so whatever was reported on the way here did
          // not stop it. Clear the note so a recovered boot leaves nothing
          // stale behind.
          setError(null);
        });

        // A test surface must open on a LIVE conversation, and pype persists
        // its session: reopening the tab restores the previous one. If that
        // one had ended, the pane opens on "This conversation has ended" with
        // nothing to talk to.
        //
        // Restart only for a session that was ALREADY ended when we found it —
        // tracked by whether it was ever active. Restarting on any
        // `chat_ended` would make the ended screen unreachable, so a user who
        // deliberately ends a test could never see it, and a genuine end would
        // silently reopen. Once is enough: `sawActive` is set by the restart's
        // own transition, so this cannot loop.
        let sawActive = false;

        // `restart` is gated on the WIDGET reporting `ready || ended`
        // (`READY_OR_ENDED_COMMANDS` in the loader), and that flag arrives on
        // a separate `pype-view-state` message from `<ps-pype>`. The loader's
        // own `state:change` can therefore reach `chat_ended` a beat before
        // `can('restart')` becomes true — and nothing re-emits `state:change`
        // when it does, so a single attempt at that instant is a coin flip
        // that lands wrong. Retry briefly instead of asking once.
        const tryRestart = () => {
          if (cancelled) return;
          if (loader.can('restart')) {
            loader.restart();
            return;
          }
          if (restartAttempts >= MAX_RESTART_ATTEMPTS) {
            // eslint-disable-next-line no-console
            console.warn(
              '[test-surface] restored an ended conversation and restart never became available'
            );
            return;
          }
          restartAttempts += 1;
          restartTimer = setTimeout(tryRestart, RESTART_RETRY_MS);
        };

        offState = loader.on('state:change', (state) => {
          if (cancelled) return;
          setLoaderStatus(state.status);
          // Carried on both ChatActive and ChatEnded. An ended conversation
          // still has traces worth reading — dropping the id there would make
          // the results pane go blank the moment the chat finished, which is
          // exactly when someone wants to look at it.
          if (
            state.status === LoaderStatus.ChatActive ||
            state.status === LoaderStatus.ChatEnded
          ) {
            setSession(state.session);
          }
          if (state.status === LoaderStatus.ChatActive) {
            sawActive = true;
            return;
          }
          if (state.status === LoaderStatus.ChatEnded && !sawActive) {
            sawActive = true;
            tryRestart();
          }
        });

        loader.boot().catch(() => {
          /* surfaced through onError */
        });
      })
      .catch((err: unknown) => {
        // The dynamic import itself can fail (a stale chunk after a deploy).
        // Without this catch it is an unhandled rejection, which the global
        // error boundary escalates to a full-page crash.
        failFatally(
          err instanceof Error ? err.message : 'Failed to load the Pype widget.'
        );
      });

    return () => {
      cancelled = true;
      offReady?.();
      offState?.();
      clearTimeout(restartTimer);
      if (myLoader) forceUnmount(myLoader, container);
    };
  }, [appId, channelEnv]);

  return { containerRef, status, error, loaderStatus, session };
};
